import React, { useCallback, useState } from 'react';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import { EToasterStatus, showToast } from 'utils/showToast';

import Input from 'components/Input/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from 'components/ui';

interface ICheatsDialogProps {
  cheatsOfferId: string | null;
  onSubmit: (
    variables: {
      indexCollected: number;
      playerId: string;
      offerId: string | null;
    },
    options?: {
      onSuccess?: () => void;
      onError?: () => void;
    }
  ) => void;
  onClose: () => void;
}

export const CheatsDialog = ({
  cheatsOfferId,
  onSubmit,
  onClose
}: ICheatsDialogProps): React.ReactElement | null => {
  const [playerId, setPlayerId] = useState('');
  const [subOffer, setSubOffer] = useState(1);

  const handleClose = useCallback((): void => {
    setPlayerId('');
    setSubOffer(1);
    onClose();
  }, [onClose]);

  const handleRun = useCallback((): void => {
    onSubmit(
      {
        indexCollected: subOffer - 1,
        playerId,
        offerId: cheatsOfferId
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Cheats successfully created',
            status: EToasterStatus.SUCCESS
          });
          handleClose();
        },
        onError: () => {
          showToast({
            message: 'Error creating cheats',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  }, [subOffer, playerId, cheatsOfferId, onSubmit, handleClose]);

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handlePlayerIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setPlayerId(e.target.value);
    },
    []
  );

  const handleSubOfferChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setSubOffer(Number(e.target.value));
    },
    []
  );

  return (
    <Dialog open={!!cheatsOfferId} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white p-0 max-w-[600px] rounded-lg">
        <DialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b border-base-border">
          <DialogTitle className="text-base-mutedForeground text-p-l font-normal">
            Cheats
          </DialogTitle>
          <div className="flex items-center">
            <div className="w-px h-[24px] bg-base-border mr-[16px]" />
            <FontAwesomeIcon
              icon="fa-regular fa-xmark"
              className="w-[12px] h-[14px] cursor-pointer text-base-foreground hover:text-base-mutedForeground transition-colors"
              onClick={handleClose}
            />
          </div>
        </DialogHeader>
        <div className="px-[84px] pt-[25px] pb-[40px]">
          <h2 className="text-[20px] font-semibold text-base-foreground mb-[6px]">
            Move Player to Sub Offer
          </h2>
          <p className="text-[16px] font-normal text-base-mutedForeground mb-[20px]">
            Enter Player ID and select the destination offer
          </p>

          <Input
            label="Player ID"
            placeholder="ID number"
            value={playerId}
            onChange={handlePlayerIdChange}
            className="mb-[20px]"
          />

          <div className="flex items-end gap-[8px]">
            <Input
              label="Move to"
              placeholder="Enter sub offer number"
              value={subOffer}
              onChange={handleSubOfferChange}
              type="number"
              className="flex-1"
            />
            <Button
              onClick={handleRun}
              disabled={!playerId}
              className="w-[77px] h-[40px] bg-base-foreground hover:bg-base-foreground/90 text-white"
            >
              Run
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
